import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Plus, CheckCircle, Clock, TrendingUp, Wallet } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  pending_creation: { label: 'PENDING SETUP',   color: '#FFB000', Icon: Clock },
  applied:          { label: 'APPLIED',          color: '#2979FF', Icon: Clock },
  approved:         { label: 'APPROVED',         color: '#00FF94', Icon: CheckCircle },
  active:           { label: 'ACTIVE',           color: '#00FF94', Icon: CheckCircle },
  payout_requested: { label: 'PAYOUT PENDING',   color: '#FFB000', Icon: Clock },
  completed:        { label: 'COMPLETED',        color: '#52525B', Icon: CheckCircle },
};

const CRYPTO_NETWORKS = ['ETH', 'BSC', 'TRON', 'SOL', 'BTC'];
const CRYPTO_TOKENS   = {
  ETH:   ['USDC', 'USDT', 'ETH'],
  BSC:   ['USDC', 'USDT', 'BNB'],
  TRON:  ['USDT', 'USDC', 'TRX'],
  SOL:   ['USDC', 'USDT', 'SOL'],
  BTC:   ['BTC'],
};

const SPLIT_SCHEDULE = [
  { n: 1, pct: 50,  label: '1st withdrawal' },
  { n: 2, pct: 60,  label: '2nd withdrawal' },
  { n: 3, pct: 70,  label: '3rd withdrawal' },
  { n: 4, pct: 80,  label: '4th+ withdrawal' },
];

const DEFAULT_ACCOUNT_RULES = [
  { key: 'max_daily',   icon: '📉', label: 'Max Daily Drawdown',    value: '4%',       desc: 'Account cannot lose more than 4% of balance in a single trading day' },
  { key: 'max_overall', icon: '🔻', label: 'Max Overall Drawdown',  value: '8%',       desc: 'Account cannot lose more than 8% of initial account balance in total' },
  { key: 'consistency', icon: '📊', label: 'Consistency Rule',      value: '15%',      desc: 'No single trading day can represent more than 15% of your total target profit' },
  { key: 'min_days',    icon: '⏱️', label: 'Min Trading Days',       value: 'None',     desc: 'No minimum number of trading days required before requesting a payout' },
  { key: 'profit',      icon: '🎯', label: 'Profit Target',          value: '9%+',      desc: 'Minimum 9% profit required to request a payout withdrawal' },
];

// Build rules from the first active account so the user always sees THEIR
// actual rule values (admin can override per-account). Falls back to defaults
// when the user has no funded account yet.
function rulesFromAccount(acct) {
  if (!acct) return DEFAULT_ACCOUNT_RULES;
  const dd  = acct.daily_drawdown_limit_pct ?? 4;
  const md  = acct.max_drawdown_limit_pct ?? 8;
  const con = acct.consistency_pct ?? acct.consistency_required_pct ?? 15;
  const tgt = acct.min_profit_target_pct ?? 9;
  return [
    { key: 'max_daily',   icon: '📉', label: 'Max Daily Drawdown',    value: `${dd}%`,
      desc: `Account cannot lose more than ${dd}% of balance in a single trading day` },
    { key: 'max_overall', icon: '🔻', label: 'Max Overall Drawdown',  value: `${md}%`,
      desc: `Account cannot lose more than ${md}% of initial account balance in total` },
    { key: 'consistency', icon: '📊', label: 'Consistency Rule',      value: `${con}%`,
      desc: `No single trading day can represent more than ${con}% of your total profit` },
    { key: 'min_days',    icon: '⏱️', label: 'Min Trading Days',       value: 'None',
      desc: 'No minimum number of trading days required before requesting a payout' },
    { key: 'profit',      icon: '🎯', label: 'Profit Target',          value: `${tgt}%+`,
      desc: `Minimum ${tgt}% profit required to request a payout withdrawal` },
  ];
}

const INPUT = { width: '100%', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)', padding: '10px 12px', fontSize: '14px', outline: 'none', borderRadius: 0, fontFamily: 'inherit' };
const LABEL = { display: 'block', fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--pp-text-3)', marginBottom: '6px' };

// ── Payout Modal ─────────────────────────────────────────────────────────────
function PayoutModal({ account, onClose, onSuccess }) {
  const splitPct    = account.payout_split_pct || 50;
  const maxAmount   = account.total_profit || 0;
  const [amount, setAmount]           = useState(maxAmount.toFixed(2));
  const [method, setMethod]           = useState('bank_transfer');
  const [network, setNetwork]         = useState('ETH');
  const [token, setToken]             = useState('USDC');
  const [walletAddress, setWalletAddress] = useState('');
  const [notes, setNotes]             = useState('');
  const [loading, setLoading]         = useState(false);

  const traderShare = Math.round(parseFloat(amount || 0) * splitPct / 100 * 100) / 100;

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    if (method === 'crypto' && !walletAddress.trim()) { toast.error('Wallet address is required for crypto payout'); return; }
    setLoading(true);
    try {
      const res = await api.requestPayout(account.funded_account_id, {
        amount: parseFloat(amount),
        payout_method: method,
        notes,
        crypto_network: method === 'crypto' ? network : undefined,
        crypto_token:   method === 'crypto' ? token   : undefined,
        wallet_address: method === 'crypto' ? walletAddress.trim() : undefined,
      });
      toast.success(res.message || 'Payout request submitted!');
      onSuccess();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 600, background: 'var(--pp-surface)', borderTop: '1px solid var(--pp-border)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-surface)' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Request Payout</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 22 }}>×</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Account info */}
          <div style={{ background: 'var(--pp-overlay)', padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>{account.label}</span>
            <span className="mono" style={{ fontSize: 12, color: '#00FF94', fontWeight: 700 }}>
              Profit: +${maxAmount.toFixed(2)} ({account.profit_pct?.toFixed(2)}%)
            </span>
          </div>

          {/* Profit split info */}
          <div style={{ background: 'rgba(41,121,255,0.06)', border: '1px solid rgba(41,121,255,0.2)', padding: 14, marginBottom: 16 }}>
            <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: '#2979FF' }}>
              Withdrawal #{account.next_withdrawal_number} — Your Split: <strong>{splitPct}%</strong>
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {SPLIT_SCHEDULE.map(s => (
                <div key={s.n} style={{ flex: 1, minWidth: 70, textAlign: 'center', padding: '6px 8px', background: account.next_withdrawal_number === s.n ? 'rgba(41,121,255,0.2)' : 'transparent', border: `1px solid ${account.next_withdrawal_number === s.n ? '#2979FF' : 'var(--pp-border)'}` }}>
                  <p className="mono" style={{ margin: 0, fontSize: 16, fontWeight: 800, color: account.next_withdrawal_number === s.n ? '#2979FF' : 'var(--pp-text-3)' }}>{s.pct}%</p>
                  <p style={{ margin: 0, fontSize: 8, color: 'var(--pp-text-3)', letterSpacing: '0.05em' }}>{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Amount */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL}>Amount to Withdraw ($)</label>
            <input style={INPUT} type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>

          {/* You receive */}
          <div style={{ background: 'rgba(0,255,148,0.06)', border: '1px solid rgba(0,255,148,0.2)', padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--pp-text-2)' }}>You receive ({splitPct}%)</span>
            <span className="mono" style={{ fontSize: 20, fontWeight: 800, color: '#00FF94' }}>${traderShare.toFixed(2)}</span>
          </div>

          {/* Payout method */}
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL}>Payout Method</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { id: 'bank_transfer', label: '🏦 Bank Transfer' },
                { id: 'crypto',        label: '🪙 Crypto' },
              ].map(m => (
                <button key={m.id} onClick={() => setMethod(m.id)} style={{ flex: 1, padding: '10px 8px', background: method === m.id ? 'rgba(41,121,255,0.15)' : 'transparent', border: `1px solid ${method === m.id ? '#2979FF' : 'var(--pp-border)'}`, color: method === m.id ? '#2979FF' : 'var(--pp-text-2)', fontSize: 13, cursor: 'pointer', fontWeight: method === m.id ? 700 : 400 }}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Crypto fields */}
          {method === 'crypto' && (
            <div style={{ background: 'var(--pp-overlay)', padding: 14, marginBottom: 14 }}>
              <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 700, color: '#2979FF', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Wallet size={14} /> Crypto Payout Details
              </p>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Network</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {CRYPTO_NETWORKS.map(n => (
                    <button key={n} onClick={() => { setNetwork(n); setToken(CRYPTO_TOKENS[n][0]); }} style={{ padding: '6px 12px', background: network === n ? '#2979FF' : 'transparent', border: `1px solid ${network === n ? '#2979FF' : 'var(--pp-border)'}`, color: network === n ? '#000' : 'var(--pp-text-3)', fontSize: 12, fontWeight: network === n ? 700 : 400, cursor: 'pointer' }}>{n}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={LABEL}>Token</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(CRYPTO_TOKENS[network] || []).map(t => (
                    <button key={t} onClick={() => setToken(t)} style={{ padding: '6px 12px', background: token === t ? '#00FF94' : 'transparent', border: `1px solid ${token === t ? '#00FF94' : 'var(--pp-border)'}`, color: token === t ? '#000' : 'var(--pp-text-3)', fontSize: 12, fontWeight: token === t ? 700 : 400, cursor: 'pointer' }}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={LABEL}>{token} Wallet Address ({network})</label>
                <input style={{ ...INPUT, fontFamily: 'monospace', fontSize: 12 }} value={walletAddress} onChange={e => setWalletAddress(e.target.value)} placeholder={`Paste your ${token} address here...`} spellCheck={false} />
                <p style={{ margin: '5px 0 0', fontSize: 10, color: '#FF3333' }}>⚠ Double-check your address. Incorrect addresses result in permanent loss of funds.</p>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={LABEL}>Notes (optional)</label>
            <input style={INPUT} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes for your payout..." />
          </div>

          <button onClick={handleSubmit} disabled={loading} className="btn-primary" style={{ width: '100%', height: 52 }}>
            {loading ? 'SUBMITTING...' : `REQUEST PAYOUT — ${splitPct}% = $${traderShare.toFixed(2)}`}
          </button>

          <p style={{ fontSize: 10, color: 'var(--pp-text-3)', textAlign: 'center', marginTop: 10, lineHeight: 1.6 }}>
            Payouts processed within 2 business days. After approval your account balance resets and a new {`$${account.size?.toLocaleString()}`} account is created.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Link MT5 Modal ───────────────────────────────────────────────────────────
function LinkMT5Modal({ account, onClose, onSuccess }) {
  const [login, setLogin]       = useState('');
  const [password, setPassword] = useState('');
  const [server, setServer]     = useState(account.server || 'Chimara-SRV01');
  const [label, setLabel]       = useState(account.label || '');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async () => {
    if (!login || !password || !server) { toast.error('Fill in all fields'); return; }
    setLoading(true);
    try {
      await api.linkMT5ToFundedAccount(account.funded_account_id, { login, password, server, platform: 'mt5', account_label: label });
      toast.success('MT5 account linked! Copy trading will begin shortly.');
      onSuccess();
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 600, background: 'var(--pp-surface)', padding: 24, borderTop: '1px solid var(--pp-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Link MT5 Account</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--pp-text-3)', cursor: 'pointer', fontSize: 22 }}>×</button>
        </div>
        {[{ l: 'MT5 Login', v: login, set: setLogin, p: 'e.g. 40013618' }, { l: 'MT5 Password', v: password, set: setPassword, p: 'Your MT5 password', t: 'password' }, { l: 'Server', v: server, set: setServer, p: 'e.g. Chimara-SRV01' }, { l: 'Account Label (optional)', v: label, set: setLabel, p: 'e.g. My Funded Account' }].map(f => (
          <div key={f.l} style={{ marginBottom: 12 }}>
            <label style={LABEL}>{f.l}</label>
            <input style={INPUT} type={f.t || 'text'} value={f.v} onChange={e => f.set(e.target.value)} placeholder={f.p} />
          </div>
        ))}
        <button onClick={handleSubmit} disabled={loading} className="btn-primary" style={{ width: '100%', height: 52, marginTop: 8 }}>
          {loading ? 'CONNECTING...' : 'LINK & ACTIVATE'}
        </button>
      </div>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function FundedAccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts]     = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [payoutModal, setPayoutModal] = useState(null);
  const [linkModal, setLinkModal]   = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    try {
      const [accts, strats] = await Promise.all([
        api.getMyFundedAccounts().catch(() => []),
        api.getStrategies().catch(() => []),
      ]);
      setAccounts(accts || []);
      setStrategies((strats || []).filter(s => s.status === 'active'));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSubscribe = async (faId, stratId) => {
    try {
      await api.subscribeFundedAccountToStrategy(faId, stratId);
      toast.success('Strategy updated!');
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '80vh' }}>
      <div style={{ width: 32, height: 32, border: '2px solid var(--pp-primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid var(--pp-border)', position: 'sticky', top: 0, background: 'var(--pp-bg)', zIndex: 20 }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--pp-text)' }}><ArrowLeft size={20} /></button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, flex: 1 }}>FUNDED ACCOUNTS</h1>
        <button onClick={() => navigate('/link-broker')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--pp-primary)', border: 'none', color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={14} /> Apply
        </button>
      </div>

      {/* Account Rules — dynamic, reflects the user's actual per-account values
          (admin can override per-account; updates here whenever they change) */}
      <div style={{ margin: '10px 16px 0', background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 14 }}>
        <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: 'var(--pp-text-2)' }}>📋 Account Rules</p>
        {rulesFromAccount(accounts.find(a => a.status === 'active') || accounts[0]).map(rule => (
          <div key={rule.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(39,39,42,0.5)' }}>
            <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{rule.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--pp-text)' }}>{rule.label}</span>
                <span className="mono" style={{ fontSize: 13, fontWeight: 800, color: rule.value === 'None' ? 'var(--pp-success)' : '#FFB000' }}>{rule.value}</span>
              </div>
              <p style={{ margin: 0, fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>{rule.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ padding: '12px 16px 0' }}>
        {accounts.length === 0 ? (
          <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: 32, textAlign: 'center' }}>
            <Shield size={40} color="var(--pp-text-3)" style={{ marginBottom: 16 }} />
            <h3 style={{ fontWeight: 700, marginBottom: 8 }}>No Funded Accounts Yet</h3>
            <p style={{ color: 'var(--pp-text-3)', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>Apply for a ProfitPulse Funded Account and trade with up to $200,000 of firm capital.</p>
            <button onClick={() => navigate('/link-broker')} className="btn-primary" style={{ padding: '12px 24px' }}>APPLY NOW</button>
          </div>
        ) : (
          accounts.map(acct => {
            const meta        = STATUS_META[acct.status] || STATUS_META.pending_creation;
            const StatusIcon  = meta.Icon;
            const isExpanded  = expandedId === acct.funded_account_id;
            const threshold   = acct.profit_threshold_pct || 3;
            const profitPct   = acct.profit_pct || 0;
            const progress    = Math.min(100, (profitPct / threshold) * 100);
            const canPayout   = acct.withdrawal_eligible === true;
            const canAutoLink = acct.can_auto_link;
            const canLink     = ['approved', 'pending_creation', 'active'].includes(acct.status) && !acct.mt5_login && !canAutoLink;

            return (
              <div key={acct.funded_account_id} style={{ background: 'var(--pp-surface)', border: `1px solid ${acct.status === 'active' ? 'rgba(0,255,148,0.25)' : 'var(--pp-border)'}`, marginBottom: 14 }}>
                {/* Header */}
                <button onClick={() => setExpandedId(isExpanded ? null : acct.funded_account_id)} style={{ width: '100%', background: 'none', border: 'none', padding: 16, textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <StatusIcon size={14} color={meta.color} />
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: meta.color }}>{meta.label}</span>
                        {acct.account_phase === 'migrating' && (
                          <span data-testid={`migrating-badge-${acct.funded_account_id}`} style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', padding: '2px 8px', background: 'rgba(255,176,0,0.12)', border: '1px solid rgba(255,176,0,0.45)', color: '#FFB000' }}>
                            ⚙ MIGRATING
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: 15 }}>{acct.label}</p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                        {acct.account_phase === 'migrating'
                          ? 'Server details unlocked once migration is finalised'
                          : `${acct.broker_display || acct.broker} · ${acct.server_display || acct.server}`}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p className="mono" style={{ margin: 0, fontSize: 24, fontWeight: 800 }}>${(acct.balance || acct.size || 0).toLocaleString()}</p>
                      {(acct.total_profit || 0) !== 0 && (
                        <p className="mono" style={{ margin: 0, fontSize: 13, color: (acct.total_profit || 0) >= 0 ? '#00FF94' : '#FF3333' }}>
                          {(acct.total_profit || 0) >= 0 ? '+' : ''}${(acct.total_profit || 0).toFixed(2)} ({profitPct.toFixed(2)}%)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Payout Progress (per-account threshold + consistency, with $-payout teaser) */}
                  {acct.status === 'active' && (() => {
                    const eff = acct.effective_target_usd || acct.profit_target_usd || 0;
                    const profit = acct.total_profit || 0;
                    const pctEff = Math.min(100, acct.progress_pct_effective ?? acct.progress_pct_to_target ?? 0);
                    const eligible = !!acct.withdrawal_eligible;
                    return (
                      <div style={{ marginTop: 14, padding: '14px 16px', background: 'linear-gradient(135deg, rgba(41,121,255,0.10) 0%, rgba(0,255,148,0.06) 100%)', border: `1px solid ${eligible ? 'rgba(0,255,148,0.4)' : 'rgba(41,121,255,0.3)'}`, borderRadius: 0 }}>
                        {/* Headline + expected payout */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                          <div>
                            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: 'var(--pp-text-3)', letterSpacing: '0.12em' }}>PROFIT TOWARD PAYOUT</p>
                            <p className="mono" style={{ margin: '4px 0 0', fontSize: 28, fontWeight: 800, color: profit >= 0 ? '#00FF94' : '#FFB000', lineHeight: 1 }}>
                              {profit >= 0 ? '+' : ''}${profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                              of <span className="mono" style={{ fontWeight: 700, color: 'var(--pp-text-2)' }}>${eff.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> needed
                              {acct.consistency_required_pct > 0 && acct.consistency_required_total_usd > acct.profit_target_usd && (
                                <span style={{ color: '#FFB000' }}> · adj. for consistency</span>
                              )}
                            </p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#00FF94', letterSpacing: '0.12em' }}>EXPECTED PAYOUT</p>
                            <p className="mono" style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 800, color: '#00FF94', lineHeight: 1 }}>
                              ${(acct.expected_payout_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                              at <span style={{ fontWeight: 700, color: 'var(--pp-text-2)' }}>{acct.payout_split_pct}%</span> split (#{acct.next_withdrawal_number})
                            </p>
                          </div>
                        </div>

                        {/* Bigger progress bar — blue → green gradient, no red */}
                        <div style={{ position: 'relative', height: 14, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                          <div style={{
                            position: 'absolute', inset: 0,
                            width: `${pctEff}%`,
                            background: eligible
                              ? 'linear-gradient(90deg, #00FF94 0%, #00DD7A 100%)'
                              : 'linear-gradient(90deg, #2979FF 0%, #00C2FF 50%, #00FF94 100%)',
                            transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
                            boxShadow: '0 0 12px rgba(0,255,148,0.35)',
                          }} />
                          {/* Animated shine */}
                          {!eligible && pctEff > 0 && (
                            <div style={{
                              position: 'absolute', top: 0, left: `${Math.min(100, pctEff - 8)}%`, width: 16, height: '100%',
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                              filter: 'blur(2px)',
                            }} />
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11 }}>
                          <span style={{ color: 'var(--pp-text-3)' }} className="mono">{pctEff.toFixed(1)}% complete</span>
                          {eligible
                            ? <span style={{ color: '#00FF94', fontWeight: 700 }}>✓ Eligible — request payout</span>
                            : <span style={{ color: 'var(--pp-text-2)', fontWeight: 600 }}>${(acct.effective_remaining_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} more to unlock</span>}
                        </div>
                      </div>
                    );
                  })()}
                </button>

                {/* AUTO-LINK MY MT5 — visible whenever creds are stored, no need to expand */}
                {canAutoLink && (
                  <div style={{ padding: '12px 16px', background: 'rgba(0,255,148,0.07)', borderTop: '1px solid rgba(0,255,148,0.25)' }}>
                    <p style={{ margin: '0 0 8px', fontSize: 11, color: '#00FF94', fontWeight: 700, letterSpacing: '0.05em' }}>
                      ✨ Your MT5 credentials are ready — one tap to deploy
                    </p>
                    <button
                      data-testid={`auto-link-funded-top-${acct.funded_account_id}`}
                      onClick={async () => {
                        const ok = window.confirm(`Auto-link this funded account?\n\nWe'll deploy your MT5 credentials (already on file) to the trading engine. No typing needed — takes ~10 seconds.`);
                        if (!ok) return;
                        try {
                          toast.success('Linking your funded account…');
                          const r = await api.autoLinkFundedAccount(acct.funded_account_id);
                          if (r.ok) {
                            toast.success(r.message || '🎉 Linked! Refreshing…');
                            setTimeout(() => window.location.reload(), 1200);
                          } else {
                            toast.error(r.message || `Could not auto-link (${r.code || 'unknown'})`);
                          }
                        } catch (e) { toast.error(e.message); }
                      }}
                      style={{ width: '100%', height: 44, background: '#00FF94', border: 'none', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.05em' }}
                    >🔗 AUTO-LINK MY MT5</button>
                  </div>
                )}

                {/* Pending setup banner */}
                {acct.status === 'pending_creation' && (
                  <div style={{ padding: '10px 16px', background: 'rgba(255,176,0,0.08)', borderTop: '1px solid rgba(255,176,0,0.2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={14} color="#FFB000" />
                    <p style={{ margin: 0, fontSize: 12, color: '#FFB000' }}>Your account is being set up. You'll receive MT5 credentials shortly.</p>
                  </div>
                )}

                {/* Expanded content */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--pp-border)', padding: 16 }}>
                    {/* PAYOUT PROGRESS — detailed calculator */}
                    {acct.status === 'active' && (
                      <div data-testid={`payout-progress-${acct.funded_account_id}`} style={{ background: 'rgba(41,121,255,0.06)', border: '1px solid rgba(41,121,255,0.2)', padding: 16, marginBottom: 14 }}>
                        <p style={{ margin: '0 0 12px', fontSize: 11, fontWeight: 700, color: '#2979FF', letterSpacing: '0.12em' }}>📊 PAYOUT BREAKDOWN</p>

                        {/* Profit composition */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
                          <div style={{ background: 'var(--pp-overlay)', padding: 10 }}>
                            <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>STARTING BALANCE</p>
                            <p className="mono" style={{ margin: '3px 0 0', fontSize: 16, fontWeight: 700 }}>${(acct.size || 0).toLocaleString()}</p>
                          </div>
                          <div style={{ background: 'var(--pp-overlay)', padding: 10 }}>
                            <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>CURRENT BALANCE</p>
                            <p className="mono" style={{ margin: '3px 0 0', fontSize: 16, fontWeight: 700 }}>${(acct.balance || acct.size || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                          </div>
                          <div style={{ background: 'var(--pp-overlay)', padding: 10 }}>
                            <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>CLOSED PROFIT</p>
                            <p className="mono" style={{ margin: '3px 0 0', fontSize: 16, fontWeight: 700, color: (acct.closed_profit_usd || 0) >= 0 ? '#00FF94' : '#FFB000' }}>
                              {(acct.closed_profit_usd || 0) >= 0 ? '+' : ''}${(acct.closed_profit_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div style={{ background: 'var(--pp-overlay)', padding: 10 }}>
                            <p style={{ margin: 0, fontSize: 9, color: 'var(--pp-text-3)', letterSpacing: '0.1em' }}>OPEN UNREALIZED</p>
                            <p className="mono" style={{ margin: '3px 0 0', fontSize: 16, fontWeight: 700, color: (acct.unrealized_pnl_usd || 0) >= 0 ? '#00FF94' : 'var(--pp-text-3)' }}>
                              {(acct.unrealized_pnl_usd || 0) >= 0 ? '+' : ''}${(acct.unrealized_pnl_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                              {(acct.unrealized_pnl_usd || 0) < 0 && (acct.closed_profit_usd || 0) > 0 && <span style={{ display: 'block', fontSize: 9, color: 'var(--pp-text-3)', marginTop: 2 }}>(not counted — only positive open trades count)</span>}
                            </p>
                          </div>
                        </div>

                        {/* Headline target */}
                        <div style={{ background: canPayout ? 'rgba(0,255,148,0.10)' : 'rgba(41,121,255,0.10)', border: `1px solid ${canPayout ? 'rgba(0,255,148,0.3)' : 'rgba(41,121,255,0.3)'}`, padding: 12, marginBottom: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontSize: 11, color: 'var(--pp-text-3)', fontWeight: 600 }}>TARGET TO UNLOCK PAYOUT</span>
                            <span className="mono" style={{ fontSize: 18, fontWeight: 800 }}>${(acct.effective_target_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          </div>
                          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-2)' }}>
                            <span className="mono">${(acct.profit_target_usd || 0).toLocaleString()}</span> ({acct.profit_threshold_pct}% min profit)
                            {acct.consistency_required_pct > 0 && acct.consistency_required_total_usd > acct.profit_target_usd && (
                              <> ⇨ raised to <span className="mono" style={{ color: '#FFB000', fontWeight: 700 }}>${(acct.consistency_required_total_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> by consistency rule</>
                            )}
                          </p>
                        </div>

                        {/* Consistency rule breakdown */}
                        {acct.consistency_required_pct > 0 && (
                          <div style={{ marginBottom: 12, padding: 12, background: 'var(--pp-overlay)', border: `1px solid ${acct.consistency_passed ? 'rgba(0,255,148,0.25)' : 'rgba(255,176,0,0.25)'}` }}>
                            <p style={{ margin: '0 0 8px', fontSize: 10, fontWeight: 700, color: acct.consistency_passed ? '#00FF94' : '#FFB000', letterSpacing: '0.12em' }}>
                              {acct.consistency_passed ? '✓' : '⚠'} CONSISTENCY RULE — {acct.consistency_required_pct}% MAX/DAY
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: 11 }}>
                              <span style={{ color: 'var(--pp-text-3)' }}>Largest single-day profit:</span>
                              <span className="mono" style={{ fontWeight: 700, textAlign: 'right' }}>${(acct.largest_day_profit_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                              <span style={{ color: 'var(--pp-text-3)' }}>Share of total profit:</span>
                              <span className="mono" style={{ fontWeight: 700, textAlign: 'right', color: acct.consistency_passed ? '#00FF94' : '#FFB000' }}>
                                {(acct.largest_day_share_pct || 0).toFixed(1)}% / {acct.consistency_required_pct}% max
                              </span>
                              <span style={{ color: 'var(--pp-text-3)' }}>Total profit needed for rule:</span>
                              <span className="mono" style={{ fontWeight: 700, textAlign: 'right' }}>${(acct.consistency_required_total_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </div>
                            <p style={{ margin: '8px 0 0', fontSize: 10, color: 'var(--pp-text-3)', lineHeight: 1.5 }}>
                              How it works: if your biggest winning day is more than <strong>{acct.consistency_required_pct}%</strong> of total profit, you need to grow total profit so that day no longer dominates.
                              {acct.largest_day_profit_usd > 0 && <>{` Formula: Largest Day ÷ ${acct.consistency_required_pct}% = $${(acct.largest_day_profit_usd / (acct.consistency_required_pct/100)).toLocaleString(undefined, { maximumFractionDigits: 0 })} required.`}</>}
                            </p>
                            {!acct.consistency_passed && acct.consistency_remaining_usd > 0 && (
                              <p style={{ margin: '6px 0 0', fontSize: 11, color: '#FFB000', fontWeight: 700 }}>
                                💡 Need <span className="mono">${acct.consistency_remaining_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> more profit so your largest day stays within the rule.
                              </p>
                            )}
                          </div>
                        )}

                        {/* Expected payout — the dollar reward to chase */}
                        <div style={{ background: 'rgba(0,255,148,0.08)', border: '1px solid rgba(0,255,148,0.3)', padding: 14 }}>
                          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: '#00FF94', letterSpacing: '0.12em' }}>💸 PROJECTED PAYOUT (#{acct.next_withdrawal_number})</p>
                          <p className="mono" style={{ margin: '6px 0 0', fontSize: 28, fontWeight: 800, color: '#00FF94', lineHeight: 1 }}>
                            ${(acct.expected_payout_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--pp-text-2)', lineHeight: 1.5 }}>
                            That's <span style={{ fontWeight: 700, color: 'var(--pp-text)' }}>{acct.payout_split_pct}%</span> of <span className="mono">${(acct.projected_profit_for_payout_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span> — your reward when you hit the target above and pass the consistency rule.
                          </p>
                          {!canPayout && (
                            <p style={{ margin: '6px 0 0', fontSize: 10, color: 'var(--pp-text-3)' }}>
                              At today's profit (<span className="mono">${(acct.total_profit || 0).toFixed(2)}</span>), you'd take home <span className="mono" style={{ fontWeight: 700 }}>${(acct.current_profit_payout_usd || 0).toFixed(2)}</span> if eligible.
                            </p>
                          )}
                        </div>

                        {/* Final eligibility line */}
                        <div style={{ marginTop: 12, padding: 10, background: canPayout ? 'rgba(0,255,148,0.1)' : 'rgba(255,176,0,0.06)', border: `1px solid ${canPayout ? 'rgba(0,255,148,0.3)' : 'rgba(255,176,0,0.2)'}` }}>
                          <p style={{ margin: 0, fontSize: 11, color: canPayout ? '#00FF94' : '#FFB000', fontWeight: 700 }}>
                            {canPayout ? '✅ ELIGIBLE FOR WITHDRAWAL' : '🔒 PAYOUT LOCKED'}
                          </p>
                          <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-2)' }}>{acct.unlock_status_message}</p>
                        </div>
                      </div>
                    )}

                    {/* Per-account rules (admin-set custom limits) */}
                    {(acct.daily_drawdown_limit_pct || acct.max_drawdown_limit_pct || acct.account_type === 'evaluation') && (
                      <div style={{ background: 'rgba(255,176,0,0.06)', border: '1px solid rgba(255,176,0,0.2)', padding: 14, marginBottom: 14 }}>
                        <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#FFB000', letterSpacing: '0.1em' }}>ACCOUNT RULES</p>
                        {acct.account_type && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Type</span><span style={{ fontSize: 12, fontWeight: 700, color: acct.account_type === 'evaluation' ? '#FFB000' : '#00FF94', textTransform: 'uppercase' }}>{acct.account_type || 'Instant'}</span></div>}
                        {acct.daily_drawdown_limit_pct && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Daily Drawdown Limit</span><span className="mono" style={{ fontSize: 13, fontWeight: 700, color: '#FF3333' }}>{acct.daily_drawdown_limit_pct}%</span></div>}
                        {acct.max_drawdown_limit_pct && <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Max Drawdown</span><span className="mono" style={{ fontSize: 13, fontWeight: 700, color: '#FF3333' }}>{acct.max_drawdown_limit_pct}%</span></div>}
                        {acct.account_type === 'evaluation' && !acct.evaluation_passed && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(255,176,0,0.1)', border: '1px solid rgba(255,176,0,0.3)' }}>
                            <p style={{ margin: 0, fontSize: 11, color: '#FFB000', fontWeight: 600 }}>Strategy copying locked until profit target is reached</p>
                          </div>
                        )}
                      </div>
                    )}
                    {/* MT5 details if linked */}
                    {acct.mt5_login && (
                      <div style={{ background: 'var(--pp-overlay)', padding: 12, marginBottom: 14 }}>
                        <p className="section-label" style={{ marginBottom: 8 }}>MT5 Account</p>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Login</span>
                          <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{acct.mt5_login}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--pp-text-3)' }}>Server</span>
                          <span className="mono" style={{ fontSize: 12 }}>{acct.mt5_server || acct.server}</span>
                        </div>
                      </div>
                    )}

                    {/* Next withdrawal split */}
                    {acct.status === 'active' && (
                      <div style={{ background: 'rgba(41,121,255,0.06)', border: '1px solid rgba(41,121,255,0.2)', padding: 12, marginBottom: 14 }}>
                        <p style={{ margin: 0, fontSize: 12, color: '#2979FF', fontWeight: 600 }}>
                          Withdrawal #{acct.next_withdrawal_number} — Your split: <strong>{acct.payout_split_pct}%</strong>
                        </p>
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--pp-text-3)' }}>
                          Split grows: 50% → 60% → 70% → 80% with each successful payout
                        </p>
                      </div>
                    )}

                    {/* Strategy subscriptions */}
                    <p className="section-label" style={{ marginBottom: 10 }}>Copy Strategies</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                      {strategies.map(s => {
                        const subscribed = (acct.strategy_subscriptions || []).includes(s.strategy_id);
                        return (
                          <button key={s.strategy_id} onClick={() => handleSubscribe(acct.funded_account_id, s.strategy_id)}
                            style={{ padding: '6px 12px', background: subscribed ? 'var(--pp-primary)' : 'transparent', border: `1px solid ${subscribed ? 'var(--pp-primary)' : 'var(--pp-border)'}`, color: subscribed ? '#fff' : 'var(--pp-text-2)', fontSize: 11, cursor: 'pointer', fontWeight: subscribed ? 700 : 400 }}>
                            {subscribed ? '✓ ' : ''}{s.name}
                          </button>
                        );
                      })}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 10 }}>
                      {canAutoLink && (
                        <button
                          data-testid={`auto-link-funded-${acct.funded_account_id}`}
                          onClick={async () => {
                            const ok = window.confirm(`Auto-link this funded account?\n\nWe'll deploy your MT5 credentials (already on file) to the trading engine. No typing needed — takes ~10 seconds.`);
                            if (!ok) return;
                            try {
                              toast.success('Linking your funded account…');
                              const r = await api.autoLinkFundedAccount(acct.funded_account_id);
                              if (r.ok) {
                                toast.success(r.message || '🎉 Linked! Refreshing…');
                                setTimeout(() => window.location.reload(), 1200);
                              } else {
                                toast.error(r.message || `Could not auto-link (${r.code || 'unknown'})`);
                              }
                            } catch (e) { toast.error(e.message); }
                          }}
                          style={{ flex: 1, height: 44, background: '#00FF94', border: 'none', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.05em' }}
                        >🔗 AUTO-LINK MY MT5</button>
                      )}
                      {canLink && (
                        <button onClick={() => setLinkModal(acct)} style={{ flex: 1, height: 44, background: '#2979FF', border: 'none', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>LINK MT5</button>
                      )}
                      {canPayout ? (
                        <button onClick={() => setPayoutModal(acct)} style={{ flex: 1, height: 44, background: '#00FF94', border: 'none', color: '#000', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <TrendingUp size={14} /> REQUEST PAYOUT ({acct.payout_split_pct}%)
                        </button>
                      ) : acct.status === 'active' && (
                        <div title={acct.unlock_status_message} style={{ flex: 1, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', fontSize: 11, color: 'var(--pp-text-3)', padding: '0 8px', textAlign: 'center', lineHeight: 1.3 }}>
                          🔒 {acct.unlock_status_message?.length > 50 ? acct.unlock_status_message.slice(0, 50) + '…' : acct.unlock_status_message}
                        </div>
                      )}
                    </div>

                    {/* Payout history */}
                    {(acct.payout_requests || []).length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <p className="section-label" style={{ marginBottom: 8 }}>Payout History</p>
                        {acct.payout_requests.map((p, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--pp-border)' }}>
                            <div>
                              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: '#00FF94' }}>${p.amount?.toLocaleString()}</span>
                              {p.trader_share && <span className="mono" style={{ fontSize: 11, color: 'var(--pp-text-3)', marginLeft: 8 }}>→ ${p.trader_share?.toFixed(2)} to you ({p.split_pct}%)</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ fontSize: 11, color: 'var(--pp-text-3)' }}>{p.created_at?.slice(0, 10)}</span>
                              <span className={p.status === 'paid' ? 'badge-success' : 'badge-warning'}>{(p.status || 'pending').toUpperCase()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {payoutModal && <PayoutModal account={payoutModal} onClose={() => setPayoutModal(null)} onSuccess={() => { setPayoutModal(null); load(); }} />}
      {linkModal   && <LinkMT5Modal account={linkModal} onClose={() => setLinkModal(null)} onSuccess={() => { setLinkModal(null); load(); }} />}
    </div>
  );
}
